import { AfterViewInit, Directive, ElementRef, OnDestroy, Optional, Self } from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';

// Kendo's DropDownListComponent only sets aria-controls on its host while the popup
// is open (it points at the listbox, which is itself only rendered into the DOM once
// open), so collapsed dropdowns are flagged for a missing required aria-controls
// attribute (WCAG 4.1.2). listBoxId is a stable id generated once for the widget's
// lifetime, so we keep aria-controls set to it at all times and restore it whenever
// Kendo's own binding clears it back out on close.
@Directive({
  selector: 'kendo-dropdownlist[appKendoAriaControls]',
})
export class KendoAriaControlsDirective implements AfterViewInit, OnDestroy {

  private observer?: MutationObserver;

  constructor(
    private host: ElementRef<HTMLElement>,
    @Optional() @Self() private dropdown?: DropDownListComponent
  ) { }

  ngAfterViewInit(): void {
    if (!this.dropdown) {
      return;
    }
    const el = this.host.nativeElement;
    this.applyAriaControls(el);

    this.observer = new MutationObserver(() => this.applyAriaControls(el));
    this.observer.observe(el, { attributes: true, attributeFilter: ['aria-controls'] });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private applyAriaControls(el: HTMLElement): void {
    const listBoxId = this.dropdown?.listBoxId;
    if (listBoxId && el.getAttribute('aria-controls') !== listBoxId) {
      el.setAttribute('aria-controls', listBoxId);
    }
  }
}
